import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { updateFolderAtom, deleteFolderAtom } from '../../../../atoms/v3/folderAtoms';
import { Folder } from '../../../../types/folder';

interface FolderEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  folder: Folder | null;
  projectCount?: number;
}

/**
 * 文件夹编辑/删除模态框
 * 支持重命名文件夹和删除文件夹
 */
export const FolderEditModal: React.FC<FolderEditModalProps> = ({
  isOpen,
  onClose,
  folder,
  projectCount = 0
}) => {
  const { t } = useTranslation(['v3/projects', 'v3/common']);
  const [isDark] = useAtom(isDarkAtom);
  const [, updateFolder] = useAtom(updateFolderAtom);
  const [, deleteFolder] = useAtom(deleteFolderAtom);
  
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // 当文件夹变化时更新名称
  useEffect(() => {
    if (folder) {
      setName(folder.name);
    }
    setError(null);
    setShowDeleteConfirm(false);
  }, [folder]);

  // 处理重命名
  const handleRename = async () => {
    if (!folder || !name.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const success = await updateFolder({
        id: folder.id,
        name: name.trim()
      });

      if (success) {
        onClose();
      } else {
        setError(t('v3/common:errors.saveFailed', '保存失败'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('v3/common:errors.saveFailed', '保存失败'));
    } finally {
      setIsLoading(false);
    }
  };

  // 处理删除
  const handleDelete = async () => {
    if (!folder) return;

    setIsLoading(true);
    setError(null);

    try {
      const success = await deleteFolder(folder.id);

      if (success) {
        onClose();
      } else {
        setError(t('v3/common:errors.deleteFailed', '删除失败'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('v3/common:errors.deleteFailed', '删除失败'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!folder) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* 背景遮罩 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            {/* 模态框内容 */}
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`
                w-full max-w-md rounded-ios-lg shadow-ios overflow-hidden
                ${isDark ? 'bg-ios-dark-gray5' : 'bg-white'}
              `}
              onClick={(e) => e.stopPropagation()}
            >
              {/* 标题栏 */}
              <div className={`
                px-6 py-4 border-b
                ${isDark ? 'border-ios-dark-gray4' : 'border-ios-gray5'}
              `}>
                <h2 className="text-ios-title3 font-semibold text-ios-gray dark:text-ios-dark-gray">
                  {showDeleteConfirm 
                    ? t('v3/projects:folders.deleteTitle', '删除文件夹')
                    : t('v3/projects:folders.editTitle', '编辑文件夹')
                  }
                </h2>
              </div>

              {/* 内容区域 */}
              <div className="px-6 py-4">
                {showDeleteConfirm ? (
                  // 删除确认界面
                  <div className="space-y-4">
                    <p className="text-ios-body text-ios-gray dark:text-ios-dark-gray">
                      {projectCount > 0 
                        ? t('v3/projects:folders.deleteConfirmWithProjects', 
                            '确定要删除文件夹 "{{name}}" 吗？文件夹中的 {{count}} 个项目将变为未分组。', 
                            { name: folder.name, count: projectCount })
                        : t('v3/projects:folders.deleteConfirm', 
                            '确定要删除文件夹 "{{name}}" 吗？', 
                            { name: folder.name })
                      }
                    </p>
                    <p className="text-ios-caption text-ios-gray2 dark:text-ios-dark-gray2">
                      {t('v3/projects:folders.deleteWarning', '此操作无法撤销。')}
                    </p>
                  </div>
                ) : (
                  // 编辑界面
                  <div className="space-y-4">
                    {/* 文件夹信息 */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-ios-caption text-ios-gray2 dark:text-ios-dark-gray2">
                        <span>📁</span>
                        <span>
                          {t('v3/projects:folders.projectCount', '{{count}} 个项目', { count: projectCount })}
                        </span>
                      </div>
                    </div>

                    {/* 文件夹名称输入 */}
                    <div>
                      <label className="block text-ios-subhead font-medium text-ios-gray dark:text-ios-dark-gray mb-1">
                        {t('v3/projects:folders.name', '文件夹名称')}
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className={`
                          w-full h-10 px-3 rounded-ios-md text-ios-body focus:outline-none
                          ${isDark 
                            ? 'bg-ios-dark-gray4 text-ios-dark-gray1' 
                            : 'bg-ios-gray5 text-ios-gray1'
                          }
                        `}
                        placeholder={t('v3/projects:folders.namePlaceholder', '输入文件夹名称')}
                        autoFocus
                      />
                    </div>
                  </div>
                )}

                {/* 错误信息 */}
                {error && (
                  <div className="mt-4 p-3 rounded-ios-md bg-ios-red/10 border border-ios-red/20">
                    <p className="text-ios-caption text-ios-red dark:text-ios-dark-red">
                      {error}
                    </p>
                  </div>
                )}
              </div>

              {/* 按钮区域 */}
              <div className={`
                px-6 py-4 border-t flex gap-3
                ${isDark ? 'border-ios-dark-gray4' : 'border-ios-gray5'}
              `}>
                {showDeleteConfirm ? (
                  // 删除确认按钮
                  <>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isLoading}
                      className="flex-1 h-10 rounded-ios-md text-ios-body font-medium text-ios-gray dark:text-ios-dark-gray bg-ios-gray6 dark:bg-ios-dark-gray4 hover:bg-ios-gray5 dark:hover:bg-ios-dark-gray3 transition-colors disabled:opacity-50"
                    >
                      {t('v3/common:actions.cancel', '取消')}
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={isLoading}
                      className="flex-1 h-10 rounded-ios-md text-ios-body font-medium text-white bg-ios-red hover:bg-ios-red/90 transition-colors disabled:opacity-50"
                    >
                      {isLoading ? t('v3/common:actions.deleting', '删除中...') : t('v3/common:actions.delete', '删除')}
                    </button>
                  </>
                ) : (
                  // 编辑按钮
                  <>
                    <button
                      onClick={onClose}
                      disabled={isLoading}
                      className="flex-1 h-10 rounded-ios-md text-ios-body font-medium text-ios-gray dark:text-ios-dark-gray bg-ios-gray6 dark:bg-ios-dark-gray4 hover:bg-ios-gray5 dark:hover:bg-ios-dark-gray3 transition-colors disabled:opacity-50"
                    >
                      {t('v3/common:actions.cancel', '取消')}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={isLoading}
                      className="px-4 h-10 rounded-ios-md text-ios-body font-medium text-ios-red dark:text-ios-dark-red bg-ios-red/10 hover:bg-ios-red/20 transition-colors disabled:opacity-50"
                    >
                      {t('v3/common:actions.delete', '删除')}
                    </button>
                    <button
                      onClick={handleRename}
                      disabled={isLoading || !name.trim() || name.trim() === folder.name}
                      className="flex-1 h-10 rounded-ios-md text-ios-body font-medium text-white bg-ios-blue hover:bg-ios-blue/90 transition-colors disabled:opacity-50"
                    >
                      {isLoading ? t('v3/common:actions.saving', '保存中...') : t('v3/common:actions.save', '保存')}
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
